import React from 'react';
import { hasCalendarEventField } from '../config/calendarEventTypes';
import EntityDetailDialog from '@/shared/components/EntityDetailDialog';
import RowActions from '@/shared/components/patterns/RowActions';
import StatusPill from '@/shared/components/patterns/StatusPill';
import CommentThread from '@/shared/components/comments/CommentThread';
import { getPriorityLabel } from '@/modules/calendar/utils/calendar.helpers';
import { getEventTypeLabel, getEventTypeTone, getPriorityTone } from '@/modules/calendar/config/calendarPage.config';
import ClientCustomFieldsSection from '../../clients/components/ClientCustomFieldsSection';
import { archiveCalendarCustomField, createCalendarCustomField, listCalendarCustomFields } from '../services/calendar-custom-fields.service';
import EventDetailContent, { getEventDetailType } from './EventDetailContent';
import '../styles/calendar-event-detail.css';

const CALENDAR_CUSTOM_FIELDS_API = { list: listCalendarCustomFields, create: createCalendarCustomField, archive: archiveCalendarCustomField };

function getInitials(title = '') {
  const parts = String(title).trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts.slice(0, 2).map(part => part[0]).join('').toUpperCase() : 'EV';
}

export default function EventDetailModal({
  event, isOpen, onClose, onEdit, onDelete,
  comments = [], commentsError = '', currentUser = null,
  staff = [], clients = [], suppliers = [], enabledModuleKeys,
  onAddNote, onDeleteNote, focusComments = false,
}) {
  if (!event) return null;
  const type = getEventDetailType(event);
  const editActions = !event.isReadOnly && onEdit ? <RowActions actions={[{
    id: 'edit', iconKey: 'actions.edit', label: 'Modifica evento', onClick: () => onEdit(event),
  }]} /> : null;
  const dangerActions = !event.isReadOnly && onDelete ? <RowActions actions={[{
    id: 'delete', iconKey: 'actions.delete', label: 'Elimina evento', onClick: () => onDelete(event), tone: 'danger',
  }]} /> : null;

  return <EntityDetailDialog
    isOpen={isOpen}
    onClose={onClose}
    title={event.title || 'Evento'}
    subtitle={getEventTypeLabel(type)}
    avatarLabel={getInitials(event.title)}
    className="calendar-event-detail"
    editActions={editActions}
    dangerActions={dangerActions}
    headerMeta={<div className="calendar-event-detail__badges">
      {hasCalendarEventField(type, 'priority') ? <StatusPill label={`Priorità ${getPriorityLabel(event.priority).toLowerCase()}`} tone={getPriorityTone(event.priority)} /> : null}
      {['incasso', 'pagamento'].includes(type) ? <StatusPill label={type === 'incasso' ? 'Entrata' : 'Uscita'} tone={getEventTypeTone(type)} /> : null}
      {event.isReadOnly ? <StatusPill label="Sola lettura" tone="neutral" /> : null}
    </div>}
  >
    <div className="calendar-event-detail__layout">
      <EventDetailContent event={event} staff={staff} clients={clients} suppliers={suppliers} enabledModuleKeys={enabledModuleKeys} />
      <ClientCustomFieldsSection readOnly formKey="calendar.event.create" values={event.customFields || {}} api={CALENDAR_CUSTOM_FIELDS_API} />
      <section aria-label="Commenti" className="calendar-event-detail__comments">
        {!event.isReadOnly ? <CommentThread
          key={event.id}
          autoFocus={focusComments}
          title="Commenti"
          comments={comments}
          currentUser={currentUser}
          staff={staff}
          error={commentsError}
          emptyLabel="Nessun commento presente."
          placeholder="Scrivi un commento sull'evento..."
          submitLabel="Aggiungi commento"
          onSubmitComment={onAddNote}
          onSubmitReply={(parentId, payload) => onAddNote?.({ ...payload, parentId })}
          onDeleteComment={(comment) => onDeleteNote?.(comment.id)}
          allowReplies
        /> : <>
          <h3>Commenti</h3>
          <p className="calendar-event-detail__empty">
            {event.source === 'derived' || event.financialDetails
              ? 'I commenti sono disponibili solo per eventi calendario. Gli eventi generati da pagamenti o uscite sono in sola lettura.'
              : 'I commenti di questo evento sono disponibili nel modulo Calendario.'}
          </p>
        </>}
      </section>
    </div>
  </EntityDetailDialog>;
}
